// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyNote } from "@/types";
import { formatDateTime, pressToOpen } from "@/utils";
import Avatar from "@/UI/atoms/Avatar";
import Icon from "@/UI/atoms/Icon";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    note: TyNote;
    /** Opens the note in full, to read or to change. The whole card does it. */
    onOpen?: () => void;
    onRemove?: () => void;
};

/**
 * One note somebody left on a record.
 *
 * It is a **note**, so it is set like one: four lines at a readable size with real leading, a
 * quotation mark behind the text, and the signature at the foot. The old version printed it at the
 * same thirteen pixels as a field caption with the author crammed onto one line beside a word-link
 * saying "remove" — which made the thing a person actually wrote the smallest thing on the card.
 *
 * Four lines, then it stops. The whole card opens the note in full, which is also how it is edited,
 * so a long one is never truncated in the only place it can be read.
 */
export default function NoteCard({ note, onOpen, onRemove }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const author = typeof note.client === "object" && note.client ? note.client.name : null;

    return (
        <article
            className="note-card"
            role={onOpen ? "button" : undefined}
            tabIndex={onOpen ? 0 : undefined}
            onClick={onOpen}
            onKeyDown={onOpen ? pressToOpen(onOpen) : undefined}
        >
            <span className="note-card__quote" aria-hidden="true">
                &rdquo;
            </span>

            <p className="note-card__text">{note.text}</p>

            <div className="note-card__foot">
                {/* Who wrote it is half the value of a note on a shared record, so it is a face
                    and a name rather than a line of grey text under one. */}
                <Avatar name={author ?? "?"} size={26} />

                <span className="note-card__by">
                    <span className="note-card__author">{author ?? t.crm.sources.catalog}</span>
                    <span className="note-card__when">{formatDateTime(note.createdAt, lang)}</span>
                </span>

                {onRemove && (
                    <button
                        type="button"
                        className="note-card__tool"
                        onClick={(event) => {
                            event.stopPropagation();
                            onRemove();
                        }}
                        aria-label={t.common.remove}
                        title={t.common.remove}
                    >
                        <Icon name="trash" size={14} />
                    </button>
                )}
            </div>
        </article>
    );
}
